import type { QuestionSubType } from '@mentimeter/http-clients';
import { useIdentifiedChoiceResults } from '@mentimeter/presentation-data-hooks/results/identified/choices';
import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import type { ChoicesT } from '@mentimeter/question-modules-types';
import { ChoicesPresentation } from './ChoicesPresentation';
import { TrendsDataHandler } from './TrendsDataHandler';

// controls have the same type, copy pasted for now
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type SlideStateChoices = { reveal?: boolean };

export function ChoicesPresentationDataHandler() {
  const {
    useQuestion,
    useSeries,
    useTrends,
    useSegmentation,
    useConfig,
    useSlideState,
    useResults,
    useCurrentSession,
  } = usePresentationContext();
  const {
    admin_key,
    sub_type,
    show_correct,
    choices,
    hide_results,
    percent_default,
  } = useQuestion();
  const { participation_identity_mode, participation_identity_visibility } =
    useSeries();
  const { showTrends: shouldShowTrends } = useTrends();
  const { isSegmented } = useSegmentation();

  const { mode } = useConfig();
  const [{ reveal }] = useSlideState<SlideStateChoices>();

  const shouldShowCorrect =
    show_correct && (reveal || ['screenshot', 'public', 'idle'].includes(mode));

  const results = useResults<ChoicesT>();
  const { isCurrentSessionActive } = useCurrentSession();

  const notSupportedSubTypes: QuestionSubType[] = ['dots'];

  const isSubTypeWithIdentifiedResponses =
    !notSupportedSubTypes.includes(sub_type);

  const hasIdentifiedResultsSettingOnAndVisible =
    participation_identity_mode === 'identified' &&
    participation_identity_visibility === 'everyone';

  const shouldFetchIdentifiedResults =
    hasIdentifiedResultsSettingOnAndVisible &&
    isSubTypeWithIdentifiedResponses &&
    isCurrentSessionActive;

  const { data, isLoading } = useIdentifiedChoiceResults(
    admin_key,
    shouldFetchIdentifiedResults,
  );
  const shouldShowIdentifiedResults =
    Boolean(data || isLoading) && !hide_results;

  return (
    <ChoicesPresentation
      identifiedResults={
        shouldShowIdentifiedResults ? (data?.results ?? []) : null
      }
      isSegmented={isSegmented}
      shouldShowCorrect={shouldShowCorrect}
      results={results}
      choices={choices}
      sub_type={
        sub_type as Extract<
          QuestionSubType,
          'pie' | 'donut' | 'choices' | 'dots'
        >
      }
      shouldHideResults={hide_results}
      shouldShowPercent={percent_default}
    >
      {shouldShowTrends && (
        <TrendsDataHandler choices={choices} shouldHideResults={hide_results} />
      )}
    </ChoicesPresentation>
  );
}
